import Popper from '@mui/base/Popper';
import { default as MUISelect } from '@mui/base/Select';
import Checkbox from '@mui/material/Checkbox';
import { useTheme } from '@mui/material/styles';
import { Box, styled } from '@mui/system';
import React, { useCallback, useMemo } from 'react';

import { X, ChevronDown, Filter } from 'lucide-react';
import ComplianceIconMapping from '@root/components/complianceIconMapping';
import Text from '@root/components/v2/ui-elements/Text';
import { RANDOM_LABEL_COLORS } from '@root/utilities/colors';
import Tooltip from '../Tooltip';
import './style.scss';

const cachedColors = {};

const grey = {
  50: '#f6f8fa',
  100: '#eaeef2',
  200: '#d0d7de',
  300: '#afb8c1',
  400: '#8c959f',
  500: '#6e7781',
  600: '#57606a',
  700: '#424a53',
  800: '#32383f',
  900: '#24292f',
};

// Add dark mode colors
const darkModeColors = {
  background: '#1a202c',
  border: '#4a5568',
  text: '#f7fafc',
  hoverBg: '#2d3748',
  selectedBg: '#2c5282',
  chipBg: '#2d3748',
};

const Chip = ({ options, value, onRemove }) => {
  // console.log(options, value);
  const option = useMemo(() => {
    return options.find(o => o.value === value);
  }, [options, value]);

  const handleRemoveClick = useCallback(
    e => {
      e.stopPropagation();

      if (onRemove) {
        onRemove(value);
      }
    },
    [onRemove, value],
  );

  return (
    <Box className="Select__Chip">
      <Text className="Select__Chip__Text">{option?.label}</Text>
      <button className="Select__Chip__Button" onClick={handleRemoveClick}>
        <X size={20} />
      </button>
    </Box>
  );
};

const Button = React.forwardRef(function Button(props, ref) {
  const { ownerState, className, regionDropdown, label, ...other } = props;
  const { isDarkMode } = useTheme();

  const style = useMemo(() => {
    return {
      ...(ownerState.width && { width: ownerState.width, minWidth: ownerState.minWidth }),
      ...(ownerState.height && { height: ownerState.height }),
    };
  }, [ownerState]);
  // console.log(props, 'nvj');
  return regionDropdown ? (
    <button
      className={`${className} Select__Button_CommonComponent_region ${ownerState.disabled ? 'Disabled' : ''} ${
        ownerState.complianceRuleAction ? 'ComplianceRuleActive' : ''
      }`}
      type="button"
      style={{
        ...style,
        backgroundColor: props.transparentBg ? 'transparent' : isDarkMode ? darkModeColors.background : null,
      }}
      {...other}
      ref={ref}>
      <Tooltip title={label ? label : 'Region'} isIcon={true}>
        <Box
          // disabled={!addResourceTag}
          className="text font-12 weight-600"
          sx={{
            fill: 'var(--tabbar-icons-color)',

            /* Boder & Line/Light */
            display: 'flex',
            padding: '3px 10px',
            // border: '1px solid var(--border-color-resource)',
            // boxShadow: '0px 8px 18px rgba(93, 106, 131, 0.05)',
            // margin: '0 3px 0 10px',
            borderRadius: '5px',
            cursor: 'pointer',
            transition: 'all 0s',
            alignItems: 'center',
            // background: ownerState?.value.length > 0 ? '#e6efff' : '#fff',

            '&:hover': {
              // fill: 'white !important',
              // background: '#e2e8f0',
              // path: {
              //   fill: 'white !important',
              // },
            },
          }}>
          <Filter size={20} />
          {ownerState?.value.length && ownerState?.value.length !== 0 ? (
            <span style={{ marginLeft: '5px' }}>{ownerState?.value.length}</span>
          ) : null}
        </Box>
      </Tooltip>
    </button>
  ) : (
    <button
      className={`${className} Select__Button_CommonComponent ${ownerState.disabled ? 'Disabled' : ''} ${
        ownerState.complianceRuleAction ? 'ComplianceRuleActive' : ''
      }`}
      type="button"
      style={{
        ...style,
        backgroundColor: props.transparentBg ? 'transparent' : isDarkMode ? darkModeColors.background : null,
      }}
      {...other}
      ref={ref}>
      <Text className="Select__ButtonText">{ownerState.renderValue(ownerState.value) ?? ownerState.placeholder}</Text>
      <ChevronDown size={20} />
    </button>
  );
});

// added custom scroll bar css
const StyledListbox = styled('ul')(
  ({ theme }) => `
  font-family: Manrope;
  font-size: 0.875rem;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  // margin: 12px 0;
  // min-width: 184px;
  border-radius: 5px;
  overflow: auto;
  outline: 0px;
  list-style: none;
  background: ${theme.palette.mode === 'dark' ? darkModeColors.background : '#fff'};
  border: 1px solid ${theme.palette.mode === 'dark' ? darkModeColors.border : '#CBD5E0'};
  color: ${theme.palette.mode === 'dark' ? darkModeColors.text : grey[900]};
  box-shadow: 0px 8px 18px rgba(0, 0, 0, ${theme.palette.mode === 'dark' ? '0.3' : '0.1'});
   
   &::-webkit-scrollbar-track {
    border-radius: 8px;
    background-color: ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'};
  }

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
    border-radius: 8px;
    background-color: ${theme.palette.mode === 'dark' ? '#2d3748' : '#f5f5f5'};
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 8px;
    background-color: ${theme.palette.mode === 'dark' ? '#4a5568' : '#ccc'};

    &:hover {
      background-color: ${theme.palette.mode === 'dark' ? '#718096' : '#aaa'} !important;
    }
  }
  `,
);

const CustomSelect = React.forwardRef(function CustomSelect(props, ref) {
  const { regionDropdown, fromCompliance } = props;

  const MyPopper = useCallback(
    props => {
      return (
        <Popper
          {...props}
          container={props.ownerState.isTagModalContainer ? document.querySelector('.create-tag-modal-container') : ''}
          style={{
            width: regionDropdown && !fromCompliance ? '200px' : fromCompliance ? '250px' : props.anchorEl?.clientWidth,
            marginTop: '10px',
          }}
          placement="bottom-start"
          // use this to handle select css
          // open={true}
          disableInteractive={true}
          modifiers={[
            {
              name: 'flip',
              enabled: false,
            },
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                boundary: 'viewport',
                padding: 38,
              },
            },
            {
              name: 'offset',
              options: {
                offset: [0, 10],
              },
            },
          ]}
          className={
            props.ownerState.inventoryTableDropDown
              ? 'popper-resource-table-dropdown'
              : props.ownerState.regionDropdown
              ? 'popper-zindex'
              : props.ownerState.isTagModalContainer
              ? 'tag-popper-zindex'
              : props.ownerState.isKubernetesCompliance
              ? 'KubernetesCompliance_Filter'
              : 'normal-zindex'
          }
        />
      );
    },
    [fromCompliance, regionDropdown],
  );
  const slots = useMemo(() => {
    // console.log(props, 'nkdjnfdk');
    return {
      root: Button,
      listbox: StyledListbox,
      popper: MyPopper,
      ...props.slots,
    };
  }, [MyPopper, props]);

  return <MUISelect {...props} ref={ref} slots={slots} transparentBg={props.transparentBg} sx={{ marginTop: '2px' }} />;
});

const colorMapper = element => {
  if (cachedColors[`${element}`]) {
    return cachedColors[`${element}`];
  }

  const color = RANDOM_LABEL_COLORS[Math.floor(Math.random() * RANDOM_LABEL_COLORS.length)];
  cachedColors[`${element}`] = color;
  return color;
};

function SelectNew({
  placeholder,
  options,
  value,
  enableSelectAll,
  allSelectedButtonLabel,
  width,
  height = '29px',
  minWidth,
  onChange,
  mode = 'multiple',
  enableChips = false,
  label,
  disabled = false,
  transparentBg,
  complianceRuleAction = false,
  showCheckBox = true,
  regionDropdown = false,
  isTagModalContainer = false,
  inventoryTableDropDown = false,
  isTooltipActive = true,
  isIcon = false,
  isLabelTagging = false,
  isGrandParent = false,
  fromCompliance = false,
  isKubernetesCompliance = false,
  jira_input,
}) {
  const { isDarkMode } = useTheme();

  const isAllSelected = useMemo(() => {
    if (!value || !value.length) return false;

    return options.every(o => value.some(v => v.value === o.value && v.parent === o.parent));
  }, [options, value]);

  const handleChipRemove = useCallback(
    v => {
      onChange(value.filter(_v => _v.value !== v.value || _v.parent !== v.parent));
    },
    [onChange, value],
  );
  const generateOriginalValue = useCallback(obj => {
    // console.log(obj, 'this my obj');

    const level = obj.level;
    const item1 = obj.rest.rem1;
    const item2 = obj.rest.rem2;

    if (level === 1) {
      return `${obj.value}/${item1}/${item2}`;
    } else if (level === 2) {
      return `${item1}/${obj.value}/${item2}`;
    } else {
      return `${item1}/${item2}/${obj.value}`;
    }
  }, []);

  const renderValue = useCallback(
    value => {
      if (!options.length || !value || (Array.isArray(value) && !value.length)) {
        return (
          <span className={`${jira_input ? 'jira_input' : ''} ${isDarkMode ? 'dark-mode' : ''}`}>
            {placeholder || 'Select'}
          </span>
        );
      }

      const firstOption = options.find(o => o.value === value[0]?.value);

      if (!firstOption) return null;

      if (Array.isArray(value) && value.length) {
        return (
          <div className="Select__SelectedValueContainer">
            {allSelectedButtonLabel && isAllSelected ? (
              <Text element="span" font="medium" className={jira_input ? 'jira_input' : ''}>
                {allSelectedButtonLabel}
              </Text>
            ) : (
              <>
                {enableChips ? (
                  <>
                    {value.slice(0, 2).map(v => (
                      <Chip key={v.value} value={v} options={options} onRemove={handleChipRemove} />
                    ))}

                    <Text font="semi-medium" className="Select__SelectedValue__Count">
                      {value.length > 2 ? `+${value.length - 2}` : null}
                    </Text>
                  </>
                ) : (
                  <>
                    {isTooltipActive ? (
                      <Tooltip title={firstOption.label} isIcon={true}>
                        <Text element="span" font="medium" className={`SingleLine ${jira_input ? 'jira_input' : ''}`}>
                          {firstOption.label}
                        </Text>
                      </Tooltip>
                    ) : (
                      <Text element="span" font="medium" className={`${jira_input ? 'jira_input' : ''}`}>
                        {firstOption.label}
                      </Text>
                    )}

                    <Text font="semi-medium" className="Select__SelectedValue__Count">
                      {value.length > 1 ? `+${value.length - 1}` : null}
                    </Text>
                  </>
                )}
              </>
            )}
          </div>
        );
      }

      return <span>{firstOption.label}</span>;
    },
    [
      options,
      jira_input,
      placeholder,
      allSelectedButtonLabel,
      isAllSelected,
      enableChips,
      isTooltipActive,
      handleChipRemove,
      isDarkMode,
    ],
  );

  const handleOptionClick = useCallback(
    (e, option) => {
      e.preventDefault();
      e.stopPropagation();

      if (mode === 'single') {
        onChange([
          {
            value: option.value,
            parent: option.parent,
            level: option.level,
            rest: option.rest,
            original: option.original,
            fullPath: option.fullPath,
            label: option.label,
          },
        ]);
        // Don't close dropdown with setKey
      } else {
        if (option === 'all') {
          if (isAllSelected) {
            onChange([]);
          } else {
            onChange(
              options.map(o => ({
                value: o.value,
                parent: o.parent,
                level: o.level,
                rest: o.rest,
                original: o.original,
                fullPath: o.fullPath,
                label: o.label,
              })),
            );
          }
        } else {
          // Use the same matching logic as in the checkbox
          const isSelected = value.some(
            v =>
              v.value === option.value &&
              (v.level === 3 ? generateOriginalValue(v) === option.original : v.fullPath === option.fullPath),
          );

          const updatedValue = isSelected
            ? value.filter(
                v =>
                  !(
                    v.value === option.value &&
                    (v.level === 3 ? generateOriginalValue(v) === option.original : v.fullPath === option.fullPath)
                  ),
              )
            : [
                ...value,
                {
                  value: option.value,
                  parent: option.parent,
                  level: option.level,
                  rest: option.rest,
                  original: option.original,
                  fullPath: option.fullPath,
                  label: option.label,
                },
              ];

          onChange(updatedValue);
        }
      }
    },
    [mode, onChange, isAllSelected, options, value, generateOriginalValue],
  );

  return (
    <CustomSelect
      // key={key}
      value={value}
      placeholder={placeholder}
      renderValue={renderValue}
      regionDropdown={regionDropdown}
      fromCompliance={fromCompliance}
      inventoryTableDropDown={inventoryTableDropDown}
      label={label}
      isTagModalContainer={isTagModalContainer}
      isKubernetesCompliance={isKubernetesCompliance}
      multiple
      width={width}
      height={height}
      minWidth={minWidth}
      transparentBg={transparentBg}
      complianceRuleAction={complianceRuleAction}
      disabled={disabled}
      sx={{
        marginTop: '2px',
        '& .Select__Option': {
          backgroundColor: isDarkMode ? darkModeColors.background : '#fff',
          '&:hover': {
            backgroundColor: isDarkMode ? darkModeColors.hoverBg : '#f7fafc',
          },
          '&.Selected': {
            backgroundColor: isDarkMode ? darkModeColors.selectedBg : '#e6efff',
          },
        },
        '& .Select__Chip': {
          backgroundColor: isDarkMode ? darkModeColors.chipBg : '#e6efff',
          color: isDarkMode ? darkModeColors.text : 'inherit',
        },
      }}>
      {!options.length ? (
        <Box padding="24px 16px" textAlign="center">
          <Text font="italic">No options</Text>
        </Box>
      ) : null}
      <div
        style={
          regionDropdown
            ? { maxHeight: '280px', padding: '10px' }
            : {
                minHeight: `${Math.min(options.length, 7) * 35}px`,
                maxHeight: `280px`,
              }
        }>
        {enableSelectAll && options.length ? (
          <div
            value={'all'}
            className="Select__Option"
            onMouseDown={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleOptionClick(e, 'all');
            }}
            style={{ cursor: 'pointer' }}>
            <Text element="div" className="Select__OptionLabel" font="semi-bold">
              All
            </Text>
            {mode === 'multiple' ? (
              <Checkbox
                sx={{ '&.Mui-checked': { color: '#194bfb' } }}
                className="Select__OptionCheckbox"
                checked={isAllSelected}
                onMouseDown={e => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                style={{ pointerEvents: 'none' }}
              />
            ) : null}
          </div>
        ) : null}
        <div style={{ maxHeight: '300px', zIndex: '100' }}>
          {options.map((option, idx) => (
            <div
              key={`${option.fullPath}-${idx}`}
              value={option.value}
              className={`Select__Option ${
                value.some(
                  v =>
                    v.value === option.value &&
                    (v.level === 3 ? generateOriginalValue(v) === option.original : v.fullPath === option.fullPath),
                )
                  ? 'Selected'
                  : ''
              }`}
              onMouseDown={e => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                handleOptionClick(e, option);
              }}
              style={{ cursor: 'pointer' }}>
              {isIcon && fromCompliance ? (
                <ComplianceIconMapping style={{ width: '20px', marginRight: '12px' }} name={option.value} />
              ) : null}

              {isTooltipActive ? (
                <Tooltip title={option.label}>
                  <Text style={{ width: '150px' }} className="SingleLine Select__OptionLabel" font="semi-bold">
                    {option.label === 'Google Cloud Architecture Framework' ? 'GCAF' : option.label}
                  </Text>
                </Tooltip>
              ) : (
                <Text element="div" className="Select__OptionLabel" font="semi-bold">
                  {option.label === 'Google Cloud Architecture Framework' ? 'GCAF' : option.label}
                </Text>
              )}

              {isLabelTagging && (
                <div style={{ display: 'flex', gap: '4px' }}>
                  {isGrandParent && (
                    <Tooltip title={option.grandparent} isIcon={true}>
                      <span
                        className="m-0 SingleLine"
                        style={{
                          width: '80px',
                          borderRadius: '20px',
                          marginRight: '20px',
                          color: '#fff',
                          padding: '2px 8px',
                          textAlign: 'center',
                          backgroundColor: colorMapper(option.grandparent),
                        }}>
                        {option.grandparent}
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title={option.parent} isIcon={true}>
                    <span
                      className="m-0 SingleLine"
                      style={{
                        width: '80px',
                        borderRadius: '20px',
                        color: '#fff',
                        padding: '2px 8px',
                        textAlign: 'center',
                        backgroundColor: colorMapper(option.parent),
                      }}>
                      {option.parent}
                    </span>
                  </Tooltip>
                </div>
              )}

              {showCheckBox && (
                <Checkbox
                  sx={{ '&.Mui-checked': { color: '#194bfb' } }}
                  checked={value.some(
                    v =>
                      v.value === option.value &&
                      (v.level === 3 ? generateOriginalValue(v) === option.original : v.fullPath === option.fullPath),
                  )}
                  className="Select__OptionCheckbox"
                  onMouseDown={e => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  style={{ visibility: mode === 'multiple' ? 'visible' : 'hidden', pointerEvents: 'none' }}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </CustomSelect>
  );
}

export default SelectNew;
